import React, { useEffect, useRef, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'
import { Field, InjectedFormProps, change, getFormValues, reduxForm } from 'redux-form'

import { Option } from 'constants/models'
import { STATUS_OPTIONS_VALUES } from 'services/users/constants'

import { isRequired } from 'utils/fieldValidation'

import { Form } from 'components'
import { SelectedImage } from 'components/PhotoUpload/PhotoUpload'

import i18n from 'translations'

export interface AdministrationParentSignatureFormValues {
  parent?: Option
  parentSignature?: SelectedImage
}

interface AdministrationParentSignatureModalFormProps {
  childId: number
  formValues: AdministrationParentSignatureFormValues
  isSubmitting: boolean
  onCancelClick: () => void
  onSubmit: (fields: AdministrationParentSignatureFormValues) => void
}

export const ADMINISTRATION_PARENT_SIGNATURE_FORM = 'AdministrationParentSignatureForm'

const mapState = (state) => ({
  formValues: getFormValues(ADMINISTRATION_PARENT_SIGNATURE_FORM)(state) as AdministrationParentSignatureFormValues,
})

const mapDispatch = {
  changeValue: (field, value) => change(ADMINISTRATION_PARENT_SIGNATURE_FORM, field, value),
}

const connector = connect(mapState, mapDispatch)

type PropsFromRedux = ConnectedProps<typeof connector>

type FinalProps = InjectedFormProps<
  AdministrationParentSignatureFormValues,
  AdministrationParentSignatureModalFormProps
>
  & AdministrationParentSignatureModalFormProps
  & PropsFromRedux

const AdministrationParentSignatureModalForm: React.FC<FinalProps> = ({
  changeValue,
  childId,
  formValues,
  handleSubmit,
  isSubmitting,
  onCancelClick,
  onSubmit,
}) => {
  const [isInitialized, setIsInitialized] = useState<boolean>(false)

  const parentSignatureRef = useRef<any>(null)

  const { parent } = formValues || {}

  useEffect(() => {
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    if (isInitialized && parentSignatureRef.current) {
      parentSignatureRef.current.clearSignature()
    }

    if (!parent) {
      changeValue('parentSignature', null)
    }
  }, [parent])

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Form.Row
        label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:Parent:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        <Field
          childId={childId}
          component={Form.InfiniteDropdowns.Carers}
          name="parent"
          placeholder={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:Parent:placeholder')}
          rawValue={parent}
          status={STATUS_OPTIONS_VALUES.ACTIVE}
          validate={isRequired}
          parentalResponsibilityOnly
          searchable
        />
      </Form.Row>
      {parent && (
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:ParentSignature:label')}
          required
          verticalLabel
        >
          <Field
            component={Form.SignaturePad}
            name="parentSignature"
            signatureRef={parentSignatureRef}
            validate={isRequired}
            searchable
          />
        </Form.Row>
      )}
      <Form.FooterActions
        isSubmitting={isSubmitting}
        submitLabel={i18n.t('global:Save')}
        onCancelClick={onCancelClick}
      />
    </Form>
  )
}

const enhance = compose(
  reduxForm<
    AdministrationParentSignatureFormValues,
    AdministrationParentSignatureModalFormProps
  >({ form: ADMINISTRATION_PARENT_SIGNATURE_FORM }),
  connector,
)

export default enhance(AdministrationParentSignatureModalForm)
